/**
 * Confirm the offering.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import FeeSplit from '../components/FeeSplit';
import { CardHeading, Notice, OfferingsOpeningNotice, StepsTrail } from '../components/ui';
import { useBooking } from '../context/BookingContext';
import { api } from '../lib/api';
import { formatAmount } from '../lib/format';
import { trackEvent } from '../lib/consent';

const SUPPORT_PRESETS = [500, 1000, 2000];

export default function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { draft, updateDraft, clearDraft, setLastOrder } = useBooking();
  const [email, setEmail] = useState(draft.email || '');

  // Extra support is either a preset chip or a typed custom amount, never both.
  // `mode` is the switch: clicking a chip picks 'preset', focusing or typing in
  // the custom field picks 'custom', and each clears the other.
  const initialExtra = draft.extraSupportCents || 0;
  const initialCustom = initialExtra > 0 && !SUPPORT_PRESETS.includes(initialExtra);
  const [supportMode, setSupportMode] = useState(initialCustom ? 'custom' : 'preset');
  const [presetCents, setPresetCents] = useState(initialCustom ? 0 : initialExtra);
  const [customDigits, setCustomDigits] = useState(
    initialCustom ? String(Math.round(initialExtra / 100)) : ''
  );

  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // One key per checkout visit, not per submit attempt — a retry after a
  // dropped response must reuse it so the server's idempotency check can
  // recognise a resend, rather than risk a second real order.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  if (!draft.service || !draft.monastery) {
    return <Navigate to="/explore" replace />;
  }

  const { monastery, service, option } = draft;
  const defaultDedication = t('offer.inEnglishPlaceholder', 'May all sentient beings be free from suffering and possess the causes of happiness.');
  const dedication = draft.dedicationText?.trim() || defaultDedication;

  const offeringCents = option?.amountCents ?? 0;
  const extraSupportCents = Math.min(
    supportMode === 'custom' ? (Number(customDigits) || 0) * 100 : presetCents,
    10_000_000
  );
  const totalCents = offeringCents + extraSupportCents;
  const customDisplay = customDigits === '' ? '' : Number(customDigits).toLocaleString('en-US');

  useEffect(() => {
    updateDraft({ extraSupportCents });
  }, [extraSupportCents, updateDraft]);

  const pickPreset = (cents) => {
    setSupportMode('preset');
    setCustomDigits('');
    setPresetCents((prev) => (prev === cents ? 0 : cents));
  };
  const editCustom = (raw) => {
    setSupportMode('custom');
    setPresetCents(0);
    setCustomDigits(raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 8));
  };

  const confirm = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const { order } = await api.createOrder({
        partnerId: monastery.id,
        serviceId: service.id,
        serviceOptionId: option.id,
        idempotencyKey,
        client: 'web',
        donorName: draft.donorName,
        donorEmail: email,
        dedicationText: draft.dedicationText,
        dedicationTibetan: draft.tibetanDedicationText,
        shareDedication: draft.shareDedication,
        paymentProvider: 'stripe',
        extraSupportCents,
      });
      setLastOrder(order);
      trackEvent('add_payment_info', {
        value: (order.amountCents ?? 0) / 100,
        currency: 'USD',
        transaction_id: order.reference,
        item_id: service.id,
      });
      // The dedication stays in the draft until the payment succeeds — a donor
      // who abandons the pay step and comes back should not have to rewrite it.
      navigate(`/checkout/pay/${order.reference}`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
      trackEvent('checkout_error', { item_id: service.id });
    }
  };

  return (
    <div className="shell has-cta-bar" style={{ paddingTop: 'var(--space-3xl)' }}>
      <div className="stack" style={{ marginBottom: 'var(--space-2xl)' }}>
        <button type="button" className="btn btn--quiet" style={{ justifySelf: 'start', paddingInline: 0 }} onClick={() => navigate(-1)}>
          <Icon name="arrowLeft" size={16} /> {t('checkout.backToDedication', 'Back to the dedication')}
        </button>
        <StepsTrail current={2} />
      </div>

      <form className="flow-layout" onSubmit={confirm}>
        <div className="stack stack--lg">
          <header className="stack stack--sm">
            <p className="eyebrow">{t('checkout.confirm', 'Confirm')}</p>
            <h1 className="display display--md">{t('checkout.reviewTitle', 'Review your offering')}</h1>
          </header>

          <OfferingsOpeningNotice />

          {/* --------------------------------------------- the dedication -- */}
          <section className="card card__pad">
            <CardHeading icon="quill" title={t('checkout.asItWillBeRecited', 'As it will be recited')} />
            <div className="dedication-preview">
              <p className="dedication-preview__en">“{dedication}”</p>
              {draft.tibetanDedicationText && (
                <p className="dedication-preview__bo tibetan" lang="bo">
                  {draft.tibetanDedicationText}
                </p>
              )}
              <p className="subtle">
                {t('checkout.offeredUnder', { name: draft.donorName?.trim() || t('offer.donorNamePlaceholder', 'Devoted Donor') })}
                {draft.shareDedication
                  ? ` · ${t('checkout.sharedPublicly', 'shared to the public wall')}`
                  : ` · ${t('checkout.keptPrivate', 'kept private')}`}
              </p>
            </div>
          </section>

          {/* ---------------------------------------------- extra support -- */}
          <section className="card card__pad">
            <CardHeading icon="sparkles" title={t('checkout.extraSupportTitle', 'Add extra support')}>
              <span className="card-heading__aside">{t('common.optional', 'optional')}</span>
            </CardHeading>
            <p className="subtle" style={{ marginTop: 'calc(-1 * var(--space-sm))', marginBottom: 'var(--space-lg)' }}>
              {t('checkout.extraSupportHint', 'Your contribution goes toward the offering at the monastery, GOMPA’s work, and payment processing.')}
            </p>
            <div className="support-picker">
              {SUPPORT_PRESETS.map((cents) => {
                const active = supportMode === 'preset' && presetCents === cents;
                return (
                  <button
                    type="button"
                    key={cents}
                    className={`support-chip${active ? ' is-active' : ''}`}
                    aria-pressed={active}
                    onClick={() => pickPreset(cents)}
                  >
                    {formatAmount(cents)}
                  </button>
                );
              })}
              <label className={`support-chip support-chip--custom${supportMode === 'custom' ? ' is-active' : ''}`}>
                <span aria-hidden="true">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  aria-label={t('checkout.extraSupportCustomAria', 'Custom support amount, in dollars')}
                  placeholder={t('checkout.extraSupportCustom', 'Other')}
                  value={customDisplay}
                  onFocus={() => { setSupportMode('custom'); setPresetCents(0); }}
                  onBlur={() => { if (customDigits === '') setSupportMode('preset'); }}
                  onChange={(event) => editCustom(event.target.value)}
                />
              </label>
            </div>
          </section>

          {/* -------------------------------------------------- how to pay -- */}
          <section className="card card__pad">
            <CardHeading icon="wallet" title={t('checkout.paymentStepTitle', 'Payment')} />
            <p className="subtle" style={{ marginTop: 'var(--space-sm)' }}>
              {t('checkout.paymentStepBody', 'The next step takes your card, Apple Pay or Google Pay through Stripe. Card details never reach GOMPA’s servers.')}
            </p>
          </section>

          {/* ------------------------------------------------------ contact -- */}
          <section className="card card__pad">
            <CardHeading icon="mail" title={t('checkout.emailLabel', 'Where to send the record')} />
            <div className="field">
              <label className="field__label" htmlFor="email">
                {t('checkout.emailField', 'Email')}
                <span className="subtle" style={{ fontWeight: 500 }}> · {t('common.optional', 'optional')}</span>
              </label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  updateDraft({ email: event.target.value });
                }}
                placeholder={t('checkout.emailPlaceholder', 'you@example.com')}
              />
              <p className="field__hint">
                {t('checkout.emailHint', 'Used only to send you the record of this offering and to confirm when the ritual is entered into prayer.')}
              </p>
            </div>
          </section>
        </div>

        {/* --------------------------------------------------------- summary -- */}
        <aside className="summary-card card card__pad">
          <CardHeading icon="receipt" title={t('checkout.offeringSummary', 'Offering summary')} />

          <dl>
            <div className="summary-line">
              <dt>{t('checkout.monastery', 'Monastery')}</dt>
              <dd>{monastery.name}</dd>
            </div>
            <div className="summary-line">
              <dt>{t('checkout.location', 'Location')}</dt>
              <dd>{monastery.location}</dd>
            </div>
            <div className="summary-line">
              <dt>{t('checkout.ritual', 'Ritual')}</dt>
              <dd>{service.name}</dd>
            </div>
            <div className="summary-line">
              <dt>{t('checkout.offering', 'Offering')}</dt>
              <dd>{option?.label}</dd>
            </div>
          </dl>

          {extraSupportCents > 0 && (
            <dl className="summary-breakdown">
              <div className="summary-line">
                <dt>{t('checkout.offering', 'Offering')}</dt>
                <dd>{formatAmount(offeringCents)}</dd>
              </div>
              <div className="summary-line">
                <dt>{t('checkout.extraSupportLine', 'Extra support')}</dt>
                <dd>{formatAmount(extraSupportCents)}</dd>
              </div>
            </dl>
          )}

          <div className="summary-total">
            <span className="muted" style={{ fontSize: 13 }}>{t('checkout.contribution', 'Contribution')}</span>
            <b>{formatAmount(totalCents)}</b>
          </div>

          <FeeSplit
            offeringCents={offeringCents}
            bps={monastery.platformFeeBps}
            monasteryName={monastery.name}
          />

          <label className="toggle-row" style={{ marginTop: 'var(--space-lg)' }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
            />
            <span style={{ fontSize: 12.5 }}>
              {t('checkout.termsConsent', 'I’m making this contribution as an offering for the ritual, and I have read and agree to the Terms of Offering.')}
            </span>
          </label>

          {error && (
            <div style={{ marginTop: 'var(--space-lg)' }}>
              <Notice tone="danger" title={t('checkout.orderError', 'Your offering was not placed')}>{error}</Notice>
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary btn--block btn--lg"
            style={{ marginTop: 'var(--space-lg)' }}
            disabled={!agreed || submitting}
          >
            {submitting ? (
              t('checkout.placingOffering', 'One moment…')
            ) : (
              <>
                {t('checkout.placeOffering', 'Continue to payment')}
                <Icon name="arrowRight" size={17} />
              </>
            )}
          </button>

          <p className="subtle" style={{ marginTop: 'var(--space-md)', textAlign: 'center' }}>
            {t('checkout.refundableNote', 'Fully refundable until the ritual begins.')}
          </p>
        </aside>

        <div className="cta-bar">
          <div>
            <div className="cta-bar__label">{t('checkout.contribution', 'Contribution')}</div>
            <div className="cta-bar__total">{formatAmount(totalCents)}</div>
          </div>
          <button type="submit" className="btn btn--primary" disabled={!agreed || submitting}>
            {submitting ? t('checkout.placingOffering', 'One moment…') : <>{t('checkout.placeOffering', 'Continue')} <Icon name="arrowRight" size={17} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}
